// src/utils/classroomUtils.js

// Parse everything into "YYYY-MM-DD" or null
import i18n from "i18next";

export const ymd = (d) => {
    if (d === undefined || d === null) return null;
    if (typeof d === "string") {
        const s = d.trim();
        if (!s || s.toLowerCase() === "null" || s.toLowerCase() === "none") return null;
        // ISO prefix "YYYY-MM-DD..."
        const mIso = s.match(/^\d{4}-\d{2}-\d{2}/);
        if (mIso) return mIso[0];
        // Compact "YYYYMMDD"
        const mCompact = s.match(/^(\d{4})(\d{2})(\d{2})$/);
        if (mCompact) return `${mCompact[1]}-${mCompact[2]}-${mCompact[3]}`;
        // Fallback: Date parse
        const d2 = new Date(s);
        return isNaN(d2) ? null : d2.toISOString().slice(0, 10);
    }
    if (d instanceof Date) return d.toISOString().slice(0, 10);
    if (typeof d === "number") {
        // 20250801 → parse as compact; timestamps (ms) → Date
        const s = String(d);
        if (/^\d{8}$/.test(s)) {
            return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
        }
        const d2 = new Date(d);
        return isNaN(d2) ? null : d2.toISOString().slice(0, 10);
    }
    // Last resort
    try {
        const d2 = new Date(d);
        return isNaN(d2) ? null : d2.toISOString().slice(0, 10);
    } catch {
        return null;
    }
};

// Helper: pak een datum uit meerdere mogelijke key-namen (ook nested)
const pickDate = (src, keys = []) => {
    for (const k of keys) {
        // support nested via path "classroom.start_date"
        const path = k.split(".");
        let cur = src;
        for (const p of path) {
            if (cur && Object.prototype.hasOwnProperty.call(cur, p)) {
                cur = cur[p];
            } else {
                cur = undefined;
                break;
            }
        }
        const v = ymd(cur);
        if (v) return v;
    }
    return null;
};

// Helper: pak string veld uit meerdere namen (ook nested)
const pickString = (src, keys = [], fallback = "") => {
    for (const k of keys) {
        const path = k.split(".");
        let cur = src;
        for (const p of path) {
            if (cur && Object.prototype.hasOwnProperty.call(cur, p)) {
                cur = cur[p];
            } else {
                cur = undefined;
                break;
            }
        }
        if (cur !== undefined && cur !== null) {
            const s = String(cur).trim();
            if (s && s.toLowerCase() !== "null" && s.toLowerCase() !== "none") return s;
        }
    }
    return fallback;
};

// Normaliseer 1 item naar eenduidige keys
export const normalizeClassroom = (raw = {}) => {
    const src = raw;

    // ID's gescheiden houden
    const assignmentId =
        src.id ??               // id van de teacher_classroom koppeling (vaak zo)
        src.assignmentId ??
        src.assignment_id ??
        null;

    const classroomId =
        src.classroomId ??
        src.classroom_id ??
        src.classroom?.id ??
        src.classroom?.uuid ??
        // fallback: sommige API's sturen meteen de classroom als 'id'
        (src.className || src.class_name ? src.id ?? src.uuid : null);

    const className = pickString(src, [
        "className",
        "class_name",
        "name",
        "classroom.className",
        "classroom.class_name",
        "classroom.name",
    ], "Unknown classroom");

    const role = pickString(src, ["role", "teacher_role"], "PRIMARY");

    const startDate = pickDate(src, [
        "startDate","start_date","fromDate","from_date","start","from",
        "classroom.startDate","classroom.start_date",
    ]);

    const endDate = pickDate(src, [
        "endDate","end_date","tillDate","till_date","end","to",
        "classroom.endDate","classroom.end_date",
    ]);

    const schoolYearLabel = pickString(src, [
        "schoolYearLabel","school_year_label",
        "classroom.schoolYearLabel","classroom.school_year_label",
    ], "");

    const subjectNames = Array.isArray(src.subjectNames) ? src.subjectNames : [];
    return { assignmentId, classroomId, className, role, startDate, endDate, schoolYearLabel, subjectNames };
};

export const splitByTime = (items) => {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD

    // 1) Normaliseer en sorteer op startDate asc (nullen laatst)
    const norm = (items ?? []).map(normalizeClassroom);
    norm.sort((a, b) => (a.startDate ?? "9999-12-31").localeCompare(b.startDate ?? "9999-12-31"));

    // Alleen een expliciete einddatum sluit een toewijzing af. Een docent kan
    // meerdere klassen gelijktijdig hebben; de start van een volgende klas is
    // daarom nooit automatisch het einde van een eerdere toewijzing.
    const withEffective = norm.map((c) => ({
        ...c,
        effectiveEnd: c.endDate ?? null,
    }));

    // 3) Classificatie met effectiveEnd
    const past = [], now = [], future = [], unknown = [];

    for (const c of withEffective) {
        const s = c.startDate;
        const e = c.effectiveEnd;         // kan null zijn
        const hasAnyDate = !!s || !!c.endDate;

        if (!hasAnyDate) { unknown.push(c); continue; }

        // Volgorde:
        // - future: start > today
        if (s && s > today) { future.push(c); continue; }

        // - past: e bestaat en e < today
        if (e && e < today) { past.push(c); continue; }

        // - anders overlapt vandaag: current
        now.push(c);
    }

    const cmpAsc  = (a, b) => (a ?? "").localeCompare(b ?? "");
    const cmpDesc = (a, b) => (b ?? "").localeCompare(a ?? "");

    past.sort((a, b) =>
        cmpDesc(a.effectiveEnd ?? a.endDate ?? a.startDate, b.effectiveEnd ?? b.endDate ?? b.startDate) ||
        cmpDesc(a.startDate, b.startDate) ||
        cmpAsc(a.className, b.className)
    );

    now.sort((a, b) =>
        cmpAsc(a.effectiveEnd ?? a.endDate ?? "9999-12-31", b.effectiveEnd ?? b.endDate ?? "9999-12-31") ||
        cmpAsc(a.startDate ?? "0000-01-01", b.startDate ?? "0000-01-01") ||
        cmpAsc(a.className, b.className)
    );

    future.sort((a, b) =>
        cmpAsc(a.startDate ?? "9999-12-31", b.startDate ?? "9999-12-31") ||
        cmpAsc(a.className, b.className)
    );

    unknown.sort((a, b) => cmpAsc(a.className, b.className));

    return { past, now, future, unknown };
};

export function translateClassName(classroomOrName) {
  if (!classroomOrName) return "";

  const name =
    typeof classroomOrName === "string"
      ? classroomOrName
      : classroomOrName.className || classroomOrName.name || classroomOrName.classroomName;

  if (!name && typeof classroomOrName === "object") {
    const classType = String(classroomOrName.classType || classroomOrName.type || "").toUpperCase();
    const grade = classroomOrName.grade ?? classroomOrName.classGrade ?? "";

    if (classType === "KINDERGARTEN" && grade !== "") {
      return i18n.t("classrooms.kindergarten", { number: grade });
    }

    if (classType === "GRADE" && grade !== "") {
      return i18n.t("classrooms.grade", { number: grade });
    }
  }

  if (!name) return "";

  const lower = name.toLowerCase();
  if (lower.includes("kindergarten") || lower.includes("kleuterklas")) {
    const base = String(i18n.t("classrooms.kindergarten", { number: "" })).trim();
    const suffix = name.replace(/\b(?:kindergarten|kleuterklas)\b/gi, "").trim();
    return [base, suffix].filter(Boolean).join(" ");
  }

  if (lower.includes("grade") || lower.includes("groep") || lower.includes("klas")) {
    const base = String(i18n.t("classrooms.grade", { number: "" })).trim();
    const suffix = name.replace(/\b(?:grade|groep|klas)\b/gi, "").trim();
    return [base, suffix].filter(Boolean).join(" ");
  }

  return name;
}
